import { useEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink, useNavigate, useMatch } from 'react-router-dom';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { MarketProvider, inPortals, PORTAL_KEYS, STAFF_VIEW_KEY, STAFF_VIEW_EVENT } from '../../contexts/MarketContext';
import PortalSwitcher from '../../components/PortalSwitcher';
import Requests from './Requests';
import RequestDetail from './RequestDetail';
import Suppliers from './Suppliers';
import Locations from './Locations';
import Resources from './Resources';
import Users from './Users';
import EmailLogs from './EmailLogs';
import Invoices from './Invoices';
import SupplierInvoices from './SupplierInvoices';
import StaffNewRequest from './NewRequest';
import PriceCodes from './PriceCodes';
import AdminSettings from './AdminSettings';
import UserSettings from './UserSettings';
import DataCleanup from './DataCleanup';
import TourCalendar from './TourCalendar';
import FinancialDashboard from './FinancialDashboard';
import Expenses from './Expenses';
import DashboardHome from './DashboardHome';
import GoalsPage from './GoalsPage';
import GoalsSplash from '../../components/GoalsSplash';
import GroundStaff from './GroundStaff';
import TeamDatabasePage from './TeamDatabasePage';
import FeedbackOverview from './FeedbackOverview';
import ParticipantsOverview from './ParticipantsOverview';
import StatusPipelineOverview from './StatusPipelineOverview';

const NAV_ICONS = {
  '/staff':                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
  '/staff/goals':            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></svg>,
  '/staff/calendar':         <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  '/staff/requests':         <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /></svg>,
  '/staff/invoices':          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>,
  '/staff/supplier-invoices': <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>,
  '/staff/suppliers':        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  '/staff/locations':        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  '/staff/resources':        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
  '/staff/users':            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
  '/staff/price-codes':      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>,
  '/staff/admin-settings':   <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  '/staff/financials':       <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  '/staff/expenses':         <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  '/staff/email-logs':       <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  '/staff/data-cleanup':     <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
  '/staff/ground-staff':    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c0 .552.224 1.052.586 1.414" /></svg>,
  '/staff/team-database':   <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  '/staff/feedback':        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
  '/staff/participants-overview': <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  '/staff/status-pipeline':       <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>,
};

function SidebarLink({ to, label, badge, end, collapsed, forceInactive, forceActive }) {
  const icon = NAV_ICONS[to] || null;
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive: matched }) => {
        const isActive = forceActive || (matched && !forceInactive);
        return `relative flex items-center gap-[11px] rounded-[9px] text-[13.5px] font-semibold transition-colors ${
          collapsed ? 'px-[10px] py-[9px] justify-center' : 'px-[11px] py-[9px]'
        } ${
          isActive
            ? 'bg-blue-light text-pb'
            : 'text-[#6B7280] hover:bg-surface hover:text-[#111827]'
        }`;
      }}
    >
      {({ isActive: matched }) => {
        const isActive = forceActive || (matched && !forceInactive);
        return (
        <>
          {isActive && !collapsed && (
            <span className="absolute left-0 top-[8px] bottom-[8px] w-[3px] bg-pb rounded-r-full" />
          )}
          {icon && (
            <span className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-pb' : ''}`}>
              {icon}
            </span>
          )}
          {!collapsed && <span className="flex-1 truncate">{label}</span>}
          {!collapsed && badge != null && badge > 0 && (
            <span className={`text-[10.5px] font-extrabold px-[7px] py-0.5 rounded-[20px] text-white ml-auto flex-shrink-0 ${
              isActive ? 'bg-pb' : 'bg-ba'
            }`}>
              {badge}
            </span>
          )}
          {collapsed && badge != null && badge > 0 && (
            <span className="absolute top-1 right-1 w-[6px] h-[6px] rounded-full bg-ba" />
          )}
        </>
        );
      }}
    </NavLink>
  );
}

const SIDEBAR_COLLAPSED_KEY = 'mst_sidebar_collapsed';
// The /staff shell hosts the UK → Spain portal and, via ?view=all, the combined
// "All Tours" roll-up. The PortalSwitcher in the topbar drives navigation
// between this and the other portal shells.

export default function StaffDashboard() {
  const { currentUser, logout, isAdmin, userRoles, userPortals, hasPortal } = useAuth();
  const isTourLeaderToo = userRoles.includes('tour_leader');
  const navigate = useNavigate();
  const tourDetailMatch = useMatch('/staff/requests/:id');
  const [submittedCount, setSubmittedCount] = useState(0);
  // The currently-open tour, when it's still a CRM-in-progress record — used to
  // stop the "Tours" nav item lighting up while viewing something that still
  // belongs to the CRM (see the same logic in RequestDetail.js's breadcrumb).
  const [currentTourIsCrmOrigin, setCurrentTourIsCrmOrigin] = useState(false);
  // The /staff shell shows the UK → Spain portal, or the combined "All Tours"
  // roll-up. That choice is held in localStorage (not the URL) so it survives
  // navigating between sections; the PortalSwitcher flips it and fires an event.
  const [staffView, setStaffView] = useState(() => {
    try { return localStorage.getItem(STAFF_VIEW_KEY) || 'uk_to_spain'; } catch { return 'uk_to_spain'; }
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  // The persisted collapse preference only applies to the static desktop rail.
  // On mobile the sidebar is a full-width overlay drawer (sidebarOpen), and its
  // collapse toggle isn't shown there, so a collapsed value carried over from
  // desktop must not force an icon-only, unescapable drawer.
  const effectiveCollapsed = sidebarOpen ? false : sidebarCollapsed;
  const userMenuRef = useRef(null);

  // Combined "All Tours" is available to admins and 2+‑portal users. A user who
  // can't see UK → Spain (here only for the roll-up) always gets "All".
  const canUK = isAdmin || hasPortal('uk_to_spain');
  const canAll = isAdmin || (userPortals || []).length >= 2;
  const showAll = canAll && (staffView === 'all' || !canUK);
  const activePortals = showAll ? (isAdmin ? PORTAL_KEYS : userPortals) : ['uk_to_spain'];
  const activePortalsKey = activePortals.join(',');
  // Tours created from /staff belong to the UK → Spain portal.
  const createMarket = 'uk_to_spain';

  function toggleCollapsed() {
    setSidebarCollapsed((c) => {
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(!c)); } catch {}
      return !c;
    });
  }

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'requests')), (snap) => {
      // New tours = category is sales_admin and initial_interest not yet ticked,
      // scoped to whichever market the portal is currently showing.
      setSubmittedCount(snap.docs.filter((d) => {
        const data = d.data();
        return inPortals(data, activePortals)
          && data.category === 'sales_admin' && !data.stageProgress?.initial_interest
          && (!data.crmOrigin || data.crmFullyConverted);
      }).length);

      const openId = tourDetailMatch?.params?.id;
      const openDoc = openId && snap.docs.find((d) => d.id === openId);
      const openData = openDoc?.data();
      setCurrentTourIsCrmOrigin(Boolean(openData?.crmOrigin && !openData?.crmFullyConverted));
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePortalsKey, tourDetailMatch?.params?.id]);

  useEffect(() => {
    function handler(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Re-read the /staff view when the PortalSwitcher flips it (same-tab).
  useEffect(() => {
    function onViewChange() {
      try { setStaffView(localStorage.getItem(STAFF_VIEW_KEY) || 'uk_to_spain'); } catch {}
    }
    window.addEventListener(STAFF_VIEW_EVENT, onViewChange);
    return () => window.removeEventListener(STAFF_VIEW_EVENT, onViewChange);
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';

  const MAIN_NAV = [
    { label: 'Dashboard',                to: '/staff',                 end: true },
    { label: 'Goals',                    to: '/staff/goals' },
    { label: 'Calendar',                 to: '/staff/calendar' },
    { label: 'Tours',                    to: '/staff/requests',        badge: submittedCount },
    { label: 'Databases/Sales',           to: '/staff/team-database' },
    { label: 'Invoices',                 to: '/staff/invoices' },
    { label: 'Supplier Invoices',        to: '/staff/supplier-invoices' },
    { label: 'Suppliers',                to: '/staff/suppliers' },
    { label: 'Locations',                to: '/staff/locations' },
    { label: 'Resources',                to: '/staff/resources' },
    { label: 'Users',                    to: '/staff/users' },
    { label: 'Ground Staff',              to: '/staff/ground-staff' },
    { label: 'Feedback',                  to: '/staff/feedback' },
    { label: 'Participants Overview',    to: '/staff/participants-overview' },
    { label: 'Status Pipeline',          to: '/staff/status-pipeline' },
    { label: 'Edit Categories & Fields', to: '/staff/admin-settings' },
  ];

  const currentTourId = tourDetailMatch?.params?.id;
  const ADMIN_NAV = [
    { label: 'Financials',              to: '/staff/financials' },
    { label: 'Expenses',                to: currentTourId ? `/staff/requests/${currentTourId}#expenses` : '/staff/expenses' },
    { label: 'Price Codes',             to: '/staff/price-codes' },
    { label: 'Email Logs',              to: '/staff/email-logs' },
    { label: 'Data Cleanup',            to: '/staff/data-cleanup' },
  ];

  return (
    <div className="min-h-screen bg-page flex flex-col">

      {/* ── Topbar ── */}
      <header className="h-[58px] bg-navy flex items-center justify-between px-4 flex-shrink-0 z-20">
        {/* Left: hamburger (mobile) + MST logo + divider + portal label */}
        <div className="flex items-center gap-[14px]">
          <button
            onClick={() => {
              // Desktop: collapse the sidebar to icons; mobile: open/close the drawer
              if (window.matchMedia('(min-width: 768px)').matches) toggleCollapsed();
              else setSidebarOpen(o => !o);
            }}
            className="w-9 h-9 flex items-center justify-center rounded-[9px] bg-white/10 hover:bg-white/20 transition flex-shrink-0"
            aria-label="Toggle navigation"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/MST Logo White.png" alt="MST Sports Tours" style={{ height: 26, display: 'block', flexShrink: 0 }} />
          <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,.16)', margin: '10px 0', flexShrink: 0 }} />
          <span className="hidden sm:inline text-white/[0.62] text-[13.5px] font-semibold tracking-[0.2px] whitespace-nowrap">
            Staff Portal
          </span>

          {/* Portal switcher — move between the portals this user can access. */}
          <PortalSwitcher />
        </div>

        {/* Centre: search (hidden on mobile) */}
        <div className="hidden md:flex flex-1 justify-center px-6 max-w-[460px] mx-auto">
          <div className="flex items-center gap-2 w-full h-9 rounded-[9px] px-3 bg-white/[0.09] hover:bg-white/[0.12] transition">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.55)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              placeholder="Search tours, customers, invoices…"
              className="flex-1 bg-transparent text-white text-[13px] placeholder-white/40 outline-none border-none"
              style={{ minWidth: 0 }}
            />
            <kbd className="text-[11px] text-white/40 bg-white/10 px-1.5 py-0.5 rounded-[5px] font-mono flex-shrink-0">⌘K</kbd>
          </div>
        </div>

        {/* Right: notification bell + user chip */}
        <div className="flex items-center gap-[10px]">
          {/* Notification bell */}
          <button
            aria-label={submittedCount > 0 ? `Notifications — ${submittedCount} new tours` : 'Notifications'}
            className="relative w-9 h-9 rounded-[9px] bg-white/10 flex items-center justify-center hover:bg-white/20 transition flex-shrink-0"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {submittedCount > 0 && (
              <span className="absolute top-[7px] right-[7px] w-[7px] h-[7px] rounded-full bg-ba border-2 border-navy" />
            )}
          </button>

          {/* User chip */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              aria-label="User menu"
              aria-expanded={userMenuOpen}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-[22px] px-3 py-[5px] transition"
            >
              <span className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#2C5FA0,#1A4A8A)' }}>
                {initials}
              </span>
              <div className="hidden sm:block leading-[1.15]">
                <div className="text-[12.5px] font-bold text-white whitespace-nowrap max-w-[120px] truncate">{displayName}</div>
                <div className="text-[10.5px] text-white/60">{isAdmin ? 'Admin' : 'Staff'}</div>
              </div>
              <svg className={`w-[13px] h-[13px] text-white/60 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {userMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-44 bg-white border border-[#E5E7EB] rounded-[12px] shadow-lg overflow-hidden z-50">
                <NavLink
                  to="/staff/user-settings"
                  onClick={() => setUserMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2.5 text-[13px] font-medium transition ${
                      isActive ? 'bg-blue-light text-pb' : 'text-[#374151] hover:bg-surface'
                    }`
                  }
                >
                  User Settings
                </NavLink>
                {isTourLeaderToo && (
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/tour-leader'); }}
                    className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-pb hover:bg-blue-light transition flex items-center gap-2"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                    Tour Leader View
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-dr hover:bg-red-light transition"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Below topbar ── */}
      <div className="flex flex-1 relative">

        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ── Sidebar ── */}
        {/* On mobile the sidebar renders as a full-width overlay drawer, so it always
            shows labels there — the collapse toggle below is desktop-only and has no
            way to un-collapse an icon-only rail once opened as a drawer. */}
        <aside className={`
          flex flex-shrink-0 flex-col py-3 overflow-y-auto
          bg-white border-r border-[#E5E7EB]
          fixed top-[58px] bottom-0 left-0 z-40
          transition-all duration-200 ease-in-out
          md:static md:top-auto md:bottom-auto md:z-auto md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          w-[232px] ${sidebarCollapsed ? 'md:w-[60px]' : 'md:w-[232px]'}
        `}>
          <nav className="px-2 flex flex-col gap-[2px] flex-1" onClick={() => setSidebarOpen(false)}>
            {MAIN_NAV.map(({ label, to, badge, end }) => (
              <SidebarLink
                key={to} to={to} label={label} badge={badge} end={end} collapsed={effectiveCollapsed}
                forceInactive={to.startsWith('/staff/requests') && currentTourIsCrmOrigin}
                forceActive={to === '/staff/team-database' && currentTourIsCrmOrigin}
              />
            ))}
          </nav>

          {isAdmin && (
            <>
              <div className="border-t border-[#E5E7EB] mx-[10px] my-[6px]" />
              {!effectiveCollapsed && (
                <div className="px-[13px] mb-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-[1px] text-[#9CA3AF]">
                    Admin
                  </span>
                </div>
              )}
              <nav className="px-2 flex flex-col gap-[2px]" onClick={() => setSidebarOpen(false)}>
                {ADMIN_NAV.map(({ label, to }) => (
                  <SidebarLink
                    key={to} to={to} label={label} collapsed={effectiveCollapsed}
                    forceInactive={to.startsWith('/staff/requests') && currentTourIsCrmOrigin}
                  />
                ))}
              </nav>
            </>
          )}

          {/* Status footer */}
          {!effectiveCollapsed && (
            <div className="border-t border-[#E5E7EB] mx-[10px] mt-3 pt-3 px-1 flex items-center gap-[9px]">
              <span className="w-[9px] h-[9px] rounded-full bg-sg flex-shrink-0"
                style={{ boxShadow: '0 0 0 3px rgba(26,122,74,.18)' }} />
              <div>
                <div className="text-[11.5px] font-bold text-[#374151] whitespace-nowrap">All systems live</div>
                <div className="text-[10.5px] text-[#9CA3AF]">Synced just now</div>
              </div>
            </div>
          )}

          {/* Collapse toggle — desktop only */}
          <div className="hidden md:flex border-t border-[#E5E7EB] mt-2 pt-2 px-2">
            <button
              onClick={toggleCollapsed}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="flex items-center justify-center w-full py-2 rounded-[8px] text-[#9CA3AF] hover:text-[#6B7280] hover:bg-surface transition"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              {!sidebarCollapsed && <span className="ml-2 text-[12px]">Collapse</span>}
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 px-4 py-4 md:px-[26px] md:py-[24px] overflow-y-auto min-w-0 page-fade">
          <MarketProvider portals={activePortals}>
          <GoalsSplash />
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="calendar" element={<TourCalendar />} />
            <Route path="requests" element={<Requests />} />
            <Route path="requests/new" element={<StaffNewRequest market={createMarket} basePath="/staff" />} />
            <Route path="requests/:id" element={<RequestDetail />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="supplier-invoices" element={<SupplierInvoices />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="locations" element={<Locations />} />
            <Route path="resources" element={<Resources />} />
            <Route path="users" element={<Users />} />
            <Route path="user-settings" element={<UserSettings />} />
            <Route path="admin-settings" element={<AdminSettings />} />
            <Route path="ground-staff" element={<GroundStaff />} />
            <Route path="team-database" element={<TeamDatabasePage />} />
            <Route path="feedback" element={<FeedbackOverview />} />
            <Route path="participants-overview" element={<ParticipantsOverview />} />
            <Route path="status-pipeline" element={<StatusPipelineOverview />} />
            {isAdmin && (
              <>
                <Route path="financials" element={<FinancialDashboard />} />
                <Route path="price-codes" element={<PriceCodes />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="email-logs" element={<EmailLogs />} />
                <Route path="data-cleanup" element={<DataCleanup />} />
              </>
            )}
          </Routes>
          </MarketProvider>
        </main>
      </div>
    </div>
  );
}
